import React, { useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import TableCell from "@material-ui/core/TableCell";
import { v4 as uuid } from "uuid";
import HeadlineAddButton from "./HeadlineAddButton";
import TestSeriesTableElement from "./TestSeriesTableElement";
import TestSeriesWhiteChipTableElement from "./TestSeriesWhiteChipTableElement";
import SpecimenSection from "../../../../../../api/models/test-series/SpecimenSection";
import ActionFlow from "../../../../../../api/models/test-series/ActionFlow";
import { TestSeriesContext } from "../../../../../../contexts/TestSeriesContext";
import ActionValue from "../../../../../../api/models/test-series/ActionValue";

const useStyles = makeStyles({
  tableCell: {
    border: "1px solid",
    borderColor: "#ECECED",
    verticalAlign: "top",
  },
  cellHeadline2: {
    color: "#757575",
    fontSize: "0.9rem",
    paddingTop: "10px",
    paddingBottom: "10px",
  },
});

interface TestSeriesSpecimenColumnProps {
  enabled: boolean;
  validated: boolean;
  specimen: SpecimenSection;
  updateSpecimen: (specimen: SpecimenSection) => void;
}

const TestSeriesSpecimenColumn: React.FunctionComponent<TestSeriesSpecimenColumnProps> = (props) => {
  const classes = useStyles();
  const { enabled, specimen, updateSpecimen, validated } = props;
  const { showTextDialog } = useContext(TestSeriesContext);

  function handleDeleteBuildingAction(i: number): void {
    const newArray = [...specimen.buildingActions];
    newArray.splice(i, 1);
    specimen.buildingActions = newArray;
    // trigger rerender through state update
    updateSpecimen({ ...specimen });
  }

  function handleDeleteTreatment(i: number): void {
    const newArray = [...specimen.treatments];
    newArray.splice(i, 1);
    specimen.treatments = newArray;
    // trigger rerender through state update
    updateSpecimen({ ...specimen });
  }

  function handleAddBuildingAction(newBuildingAction: ActionFlow): void {
    specimen.buildingActions.push(newBuildingAction);
    // trigger rerender through state update
    updateSpecimen({ ...specimen });
  }

  function handleAddTreatment(newTreatment: ActionFlow): void {
    specimen.treatments.push(newTreatment);
    // trigger rerender through state update
    updateSpecimen({ ...specimen });
  }

  return (
    <TableCell align="left" className={classes.tableCell}>
      <HeadlineAddButton
        title="Building Actions"
        enabled={enabled}
        onClick={(): void => {
          showTextDialog("Flow Name", undefined, "Name")
            .then((name) => {
              handleAddBuildingAction({
                name,
                actionValues: [],
              });
            })
            .catch(() => {
              // do nothing
            });
        }}
      />
      {specimen.buildingActions &&
        specimen.buildingActions.map((e: ActionFlow, i: number) => {
          return (
            <span key={uuid()}>
              <TestSeriesTableElement
                enabled={enabled}
                validated={validated}
                section="testpiece"
                flowType="PREPARATION"
                primaryChip={e.name}
                secondaryChips={e.actionValues}
                onRenamePrimary={(newName): void => {
                  e.name = newName;
                  updateSpecimen({ ...specimen });
                }}
                onPrimaryChipDelete={(): void => {
                  handleDeleteBuildingAction(i);
                }}
                updateParentState={(): void => {
                  updateSpecimen({ ...specimen });
                }}
              />
            </span>
          );
        })}
      <div className={classes.cellHeadline2}>Actions on all Specimens </div>
      <TestSeriesWhiteChipTableElement
        enabled={enabled}
        validated={validated}
        section="testpiece"
        flowType="PREPARATION"
        chips={specimen.buildingActionsOnAllSpecimen ? specimen.buildingActionsOnAllSpecimen.actionValues : []}
        updateParentState={(newChips): void => {
          if (!specimen.buildingActionsOnAllSpecimen) {
            specimen.buildingActionsOnAllSpecimen = {
              name: "",
              actionValues: newChips as ActionValue[],
            };
          }
          updateSpecimen({ ...specimen });
        }}
      />
      <HeadlineAddButton
        title="Specimen Treatment"
        enabled={enabled}
        onClick={(): void => {
          showTextDialog("Flow Name", undefined, "Name")
            .then((name) => {
              handleAddTreatment({
                name,
                actionValues: [],
              });
            })
            .catch(() => {
              // do nothing
            });
        }}
      />
      {specimen.treatments &&
        specimen.treatments.map((e: ActionFlow, i: number) => {
          return (
            <span key={uuid()}>
              <TestSeriesTableElement
                enabled={enabled}
                validated={validated}
                section="testpiece"
                flowType="TREATMENT"
                primaryChip={e.name}
                secondaryChips={e.actionValues}
                onRenamePrimary={(newName): void => {
                  e.name = newName;
                  updateSpecimen({ ...specimen });
                }}
                onPrimaryChipDelete={(): void => {
                  handleDeleteTreatment(i);
                }}
                updateParentState={(): void => {
                  updateSpecimen({ ...specimen });
                }}
              />
            </span>
          );
        })}
      <div className={classes.cellHeadline2}>Treatment on all Specimens </div>
      <TestSeriesWhiteChipTableElement
        enabled={enabled}
        validated={validated}
        section="testpiece"
        flowType="TREATMENT"
        chips={specimen.treatmentOnAllSpecimen ? specimen.treatmentOnAllSpecimen.actionValues : []}
        updateParentState={(newChips): void => {
          if (!specimen.treatmentOnAllSpecimen) {
            specimen.treatmentOnAllSpecimen = {
              name: "",
              actionValues: newChips as ActionValue[],
            };
          }
          updateSpecimen({ ...specimen });
        }}
      />
    </TableCell>
  );
};
export default TestSeriesSpecimenColumn;
